import React, { useEffect, useState } from "react";
import { Navbar, Container, Nav, Dropdown } from "react-bootstrap";
import { Button } from "@mui/material";

import { useNavigate, useLocation } from "react-router-dom";
import { useAppSelector } from "../redux/store";
import { useDispatch } from "react-redux";
import { logout } from "../redux/slices/auth";
import "./Header.css";
import { getImageUrl } from "../utilities/getImageUrl";
import { useGetUserProfileQuery } from "../redux/services/modules/user";
import { useGetSpProfileQuery } from "../redux/services/modules/service-provider";

function MenuLinesIcon() {
    return (
        <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden
        >
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
    );
}

const getInitials = (name) => {
    if (!name || typeof name !== "string") return "?";
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return (name[0] || "?").toUpperCase();
};

const Header = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const auth = useAppSelector((state) => state.auth);
    const dispatch = useDispatch();
    const [navExpanded, setNavExpanded] = useState(false);
    const handleLogout = () => {
        dispatch(logout()).then(() => {
            navigate("/login", { replace: true });
        });
    };

    const displayName = auth.user?.full_name || auth.user?.name || "User";

    const roleKey =
        auth.user?.role != null ? String(auth.user.role).toLowerCase() : "";
    const isUserRole = roleKey === "user";
    const isProviderRole = roleKey === "provider";

    const { data: userProfileData } = useGetUserProfileQuery(undefined, {
        skip: !isUserRole || !auth.user,
    });

    const { data: spProfileData } = useGetSpProfileQuery(undefined, {
        skip: !isProviderRole || !auth.user,
    });

    // Backend premium flag format: 1 => premium, 0 => not premium
    const userIsPremium =
        userProfileData?.results?.is_premium === true ||
        userProfileData?.results?.is_premium === 1 ||
        userProfileData?.results?.is_premium === "1";
    const spIsPremium =
        spProfileData?.results?.is_premium === true ||
        spProfileData?.results?.is_premium === 1 ||
        spProfileData?.results?.is_premium === "1";
    const isPremium = userIsPremium || spIsPremium;

    // Same resolution as Dashboard Account: API profile when loaded, else session user.
    const profile = isUserRole
        ? userProfileData?.results
        : isProviderRole
          ? spProfileData?.results
          : null;
    const displayProfile = profile || auth.user || {};

    // Match Account userSummary.image (default asset is a relative path — do not pass through getImageUrl).
    const avatarUrl =
        getImageUrl(displayProfile.image) ||
        getImageUrl(displayProfile.profile_image) ||
        getImageUrl(displayProfile.avatar) ||
        "../images/profile-img.jpg";
    const initials = getInitials(displayName);

    const emailToShow =
        auth.user?.email ||
        auth.user?.mail ||
        userProfileData?.results?.email ||
        userProfileData?.results?.user_email ||
        spProfileData?.results?.email ||
        spProfileData?.results?.user_email ||
        "Email";

    const path = location.pathname || "";
    const isDashboardRoute = path.startsWith("/dashboard");
    useEffect(() => {
        setNavExpanded(false);
    }, [path]);

    const isHome = path === "/" || path === "";
    const isAbout = path.startsWith("/business");
    const isSubscription = path.startsWith("/subscription");
    const isDashboard = path.startsWith("/dashboard");
    const isConnect = path.startsWith("/connect");
    const isFaq = path.startsWith("/faq");
    const isContact = path.startsWith("/contact-us");

    const brandMark = (
        <Navbar.Brand
            className="app-header-brand py-0"
            onClick={() => navigate("/", { replace: true })}
        >
            <img
                className="header-brand-logo"
                src="../images/logo.svg"
                alt="Finding Trusted Care"
            />
        </Navbar.Brand>
    );

    const renderUserDropdown = (toggleId) =>
        auth.isLoggedIn ? (
            <Dropdown align="end" className="header-user-dropdown">
                <Dropdown.Toggle
                    id={toggleId}
                    variant="link"
                    className="header-avatar-toggle d-flex align-items-center p-0 border-0 text-decoration-none"
                >
                {avatarUrl ? (
                    <img
                        src={avatarUrl}
                        alt={displayName}
                        className="header-avatar-img"
                    />
                ) : (
                    <span className="header-avatar-initials">{initials}</span>
                )}
            </Dropdown.Toggle>
            <Dropdown.Menu className="header-user-menu shadow">
                <div className="header-user-menu-header">
                    {avatarUrl ? (
                        <img
                            src={avatarUrl}
                            alt={displayName}
                            className="header-user-menu-avatar"
                        />
                    ) : (
                        <span className="header-user-menu-initials">
                            {initials}
                        </span>
                    )}
                    <p className="header-user-menu-name mb-0">{emailToShow}</p>
                </div>
                <Dropdown.Divider />
                <div className="header-user-menu-footer p-2">
                    <Button
                        variant="contained"
                        color="primary"
                        size="medium"
                        fullWidth
                        onClick={handleLogout}
                    >
                        Logout
                    </Button>
                </div>
            </Dropdown.Menu>
            </Dropdown>
        ) : null;

    const guestActions = !auth.isLoggedIn ? (
        <div className="d-flex align-items-center gap-2 app-header-guest-actions">
            {location.pathname.includes("signup") && (
                <Button
                    variant="contained"
                    color="primary"
                    size="medium"
                    onClick={() => navigate("/login", { replace: true })}
                >
                    Login
                </Button>
            )}
            {location.pathname === "/login" && (
                <Button
                    variant="contained"
                    color="primary"
                    size="medium"
                    onClick={() => navigate("/signup", { replace: true })}
                >
                    Sign Up
                </Button>
            )}
            {location.pathname !== "/login" &&
                !location.pathname.includes("signup") && (
                    <>
                        <Button
                            variant="contained"
                            color="primary"
                            size="medium"
                            onClick={() =>
                                navigate("/login", { replace: true })
                            }
                        >
                            Login
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            size="medium"
                            onClick={() =>
                                navigate("/signup", { replace: true })
                            }
                        >
                            Sign Up
                        </Button>
                    </>
                )}
        </div>
    ) : null;

    return (
        <header className="app-shell-header">
            <Navbar
                expand="md"
                expanded={navExpanded}
                onToggle={setNavExpanded}
                className="border-bottom app-header-navbar"
            >
                <Container
                    fluid
                    className="px-3 px-lg-4 app-header-navbar-inner d-flex flex-wrap align-items-center"
                >
                    <div className="d-flex d-md-none w-100 align-items-center justify-content-between app-header-mobile-toolbar">
                        {isDashboardRoute ? (
                            <div className="app-header-mobile-toolbar-dashboard w-100 d-flex align-items-center justify-content-between">
                                {brandMark}
                                <div className="d-flex align-items-center justify-content-end flex-shrink-0 app-header-mobile-trailing">
                                    <button
                                        type="button"
                                        className="app-header-icon-btn"
                                        aria-label="Open site menu"
                                        aria-controls="basic-navbar-nav"
                                        aria-expanded={navExpanded}
                                        onClick={() =>
                                            setNavExpanded((open) => !open)
                                        }
                                    >
                                        <MenuLinesIcon />
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="d-flex align-items-center flex-shrink-0">
                                    <button
                                        type="button"
                                        className="app-header-icon-btn"
                                        aria-label="Open site menu"
                                        aria-controls="basic-navbar-nav"
                                        aria-expanded={navExpanded}
                                        onClick={() =>
                                            setNavExpanded((open) => !open)
                                        }
                                    >
                                        <MenuLinesIcon />
                                    </button>
                                </div>
                                <div className="flex-grow-1 min-w-0 d-flex justify-content-center px-2">
                                    {brandMark}
                                </div>
                                <div className="d-flex align-items-center justify-content-end gap-2 flex-shrink-0 app-header-mobile-trailing">
                                    {guestActions}
                                </div>
                            </>
                        )}
                    </div>

                    <div className="app-header-brand-desktop d-none d-md-flex">
                        {brandMark}
                    </div>
                    <Navbar.Collapse
                        id="basic-navbar-nav"
                        className="app-header-collapse"
                    >
                        {auth.isLoggedIn && (
                            <div className="mobile-user-menu-section d-md-none">
                                {avatarUrl ? (
                                    <img
                                        src={avatarUrl}
                                        alt={displayName}
                                        className="mobile-user-menu-avatar"
                                    />
                                ) : (
                                    <span className="mobile-user-menu-initials">
                                        {initials}
                                    </span>
                                )}
                                <div className="mobile-user-menu-name">
                                    {emailToShow}
                                </div>
                                <div className="mobile-user-menu-actions">
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size="medium"
                                        fullWidth
                                        onClick={handleLogout}
                                    >
                                        Logout
                                    </Button>
                                </div>
                            </div>
                        )}
                        {auth.isLoggedIn && (
                            <div className="mobile-user-menu-divider d-md-none" />
                        )}
                        <Nav
                            navbar
                            className="app-header-main-nav justify-content-center"
                        >
                            <Nav.Link
                                active={isHome}
                                onClick={() => navigate("/", { replace: true })}
                            >
                                Home
                            </Nav.Link>
                            <Nav.Link
                                active={isAbout}
                                onClick={() =>
                                    navigate("/business", { replace: true })
                                }
                            >
                                About
                            </Nav.Link>
                            <Nav.Link
                                active={isSubscription}
                                onClick={() =>
                                    navigate("/subscription", {
                                        replace: true,
                                        state: { new: true },
                                    })
                                }
                            >
                                Subscription
                            </Nav.Link>
                            {auth.isLoggedIn && isPremium && (
                                <Nav.Link
                                    active={isDashboard}
                                    className="d-md-none"
                                    onClick={() =>
                                        navigate("/dashboard", {
                                            replace: true,
                                        })
                                    }
                                >
                                    Dashboard
                                </Nav.Link>
                            )}
                            <Nav.Link
                                active={isConnect}
                                onClick={() =>
                                    navigate("/connect", { replace: true })
                                }
                            >
                                {"FTC\u00A0Connect"}
                            </Nav.Link>
                            <Nav.Link
                                active={isFaq}
                                onClick={() =>
                                    navigate("/faq", { replace: true })
                                }
                            >
                                FAQ
                            </Nav.Link>
                            {auth.isLoggedIn && isPremium && (
                                <Nav.Link
                                    active={isDashboard}
                                    className="d-none d-md-block"
                                    onClick={() =>
                                        navigate("/dashboard", {
                                            replace: true,
                                        })
                                    }
                                >
                                    Dashboard
                                </Nav.Link>
                            )}
                            <Nav.Link
                                active={isContact}
                                onClick={() =>
                                    navigate("/contact-us", { replace: true })
                                }
                            >
                                Contact
                            </Nav.Link>
                        </Nav>
                    </Navbar.Collapse>
                    <div className="d-none d-md-flex align-items-center gap-2 ms-2 app-header-desktop-trailing">
                        {renderUserDropdown("header-user-menu")}
                        {guestActions}
                    </div>
                    {/* <Dropdown>
            <Dropdown.Toggle id="dropdown-basic" className="d-flex align-items-center">
            <img src="../images/user_icon.svg" className="mx-2 "/>
              User
            </Dropdown.Toggle>

            <Dropdown.Menu>
              <Dropdown.Item href="#/action-1">Action</Dropdown.Item>
              <Dropdown.Item href="#/action-2">Another action</Dropdown.Item>
              <Dropdown.Item href="#/action-3">Something else</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown> */}
                </Container>
            </Navbar>
        </header>
    );
};

export default Header;
